import businessService from '@/modules/business/services/businessService';
import { findCountryIso2ByName } from '@vcita/design-system/utils/countries';

/**
 * Business Contact Details Store
 *
 * Purpose: Manages business contact information and admin details.
 *
 * Structure:
 * - admin_info: Owner/admin account information from V1 API (business.admin_account)
 * - business_info: Business core details from V1 API (business.business), including structured address components
 *
 * State originates from platform/v1/businesses/:uid API.
 * Updates are made via v2/settings/businesses/:uid API with partial state mapping to maintain consistency.
 */
const state = {
  admin_info: {
    user_id: null,
    first_name: '',
    last_name: '',
    email: '',
    country_name: '',
    language: '', // Language code (e.g., 'en', 'es')
    display_name: '',
    phone: '',
    staff_uid: '',
  },
  business_info: {
    id: null,
    name: null,
    address: null, // Plain string for display (structured version in structured_address)
    phone: null,
    website_url: null,
    country_name: null,
    time_zone: null, // Used for scheduling (e.g., 'Bern', 'America/New_York')
    directory_id: null, // Parent directory/brand identifier
    business_category: null,
    short_description: null,
    dedicated_number: null,
    disable_analytics: null,
    scheduling_disabled: null,
    promotional: null,
    structured_address: { // Google Places API parsed object from the address field
      street_number: null,
      street_name: null,
      city: null,
      state: null,
      state_code: null,
      country: null,
      country_code: null, // ISO 3166-1 alpha-2 code
      postal_code: null,
      county: null,
      formatted_address: null,
    },
  },
};

const getters = {
  // Owner/Admin getters
  ownerFirstName: (state) => state.admin_info.first_name,
  ownerLastName: (state) => state.admin_info.last_name,
  ownerEmail: (state) => state.admin_info.email,
  ownerPhone: (state) => state.admin_info.phone,
  ownerCountryName: (state) => state.admin_info.country_name,
  ownerCountryCode: (state) => findCountryIso2ByName(state.admin_info.country_name || state.business_info.country_name),
  adminInfo: (state) => state.admin_info,
  // Business info getters
  businessInfo: (state) => state.business_info,
  businessAddress: (state) => state.business_info.address,
  businessPhone: (state) => state.business_info.phone,
  businessName: (state) => state.business_info.name,
  businessWebsite: (state) => state.business_info.website_url,
  businessCountryName: (state) => state.business_info.country_name,
  businessCountryCode: (state) => findCountryIso2ByName(state.business_info.country_name), // from the phone number prefix, always exists
  // Business structured address getters
  businessStreet: (state) => {
    const structuredAddress = state.business_info.structured_address;
    if (!structuredAddress) return null;
    // Filter removes falsy values (null/undefined) to prevent extra spaces
    const parts = [
      structuredAddress.street_number,
      structuredAddress.street_name,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  },
  businessCity: (state) => state.business_info.structured_address?.city || null,
  businessStateCode: (state) => state.business_info.structured_address?.state_code || null,
  businessPostalCode: (state) => state.business_info.structured_address?.postal_code || null,
  
};

const mutations = {
  setAdminInfo(state, adminInfo) {
    state.admin_info = { ...state.admin_info, ...adminInfo };
  },
  setBusinessInfo(state, businessInfo) {
    state.business_info = { ...state.business_info, ...businessInfo };
  },
};

const actions = {
  async fetchBusinessInfo({ commit, rootGetters }) {
    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      const { business } = await businessService.fetchBusiness(businessUid);
      if (business) {
        commit('setAdminInfo', business.admin_account);
        commit('setBusinessInfo', business.business);
      }
    } catch (e) {
      console.error('error', e);
    }
  },
  async setBusinessInfo({ rootGetters, commit }, payload) {
    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      const business = await businessService.updateBusinessSettings(businessUid, payload);

      // Since this API request is a different API than the fetchBusinessInfo
      // We should partially update the state to not override the existing data nor create inconsistencies.
      // Update only fields sent in payload + backend-auto calculated fields according to the payload (e.g structured_address)
      // In case you need to update other fields, map them below:
      if (payload.owner) {
        commit('setAdminInfo', {
          first_name: business.owner.first_name,
          last_name: business.owner.last_name,
        });
      }

      commit('setBusinessInfo', {
        address: business.address,
        phone: business.phone_info,
        country_name: business.country_name,
        structured_address: business.structured_address, // Backend-generated from address
      });
      
      return business;
    } catch (e) {
      console.error('error', e);
      return e;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
